import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Plus, Minus, Send, Printer, Pencil, Trash2, LogOut, ChevronLeft, CheckCircle2, Loader2,
} from "lucide-react";

const GOLD = "#C9A24B";
const BG = "#F6F3EC";
const DARK = "#14110E";

const eur = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;
const uuid = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function WaiterPreorder() {
  const { user, logout } = useAuth();
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({}); // dishId -> {id,name,price,quantity,notes,course}
  const [table, setTable] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const clientToken = useRef("");

  useEffect(() => {
    api.get("/preorder/menu")
      .then(({ data }) => setMenu(data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const items = useMemo(() => Object.values(cart).filter((i) => i.quantity > 0), [cart]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const bump = (d, delta) => {
    if (!clientToken.current) clientToken.current = uuid();
    setCart((c) => {
      const cur = c[d.id] || { id: d.id, name: d.name, price: d.price, quantity: 0, notes: "", course: 1 };
      const q = Math.max(0, cur.quantity + delta);
      const next = { ...c, [d.id]: { ...cur, quantity: q } };
      if (q === 0) delete next[d.id];
      return next;
    });
  };
  const setField = (id, k, v) => setCart((c) => ({ ...c, [id]: { ...c[id], [k]: v } }));

  const resetAll = () => {
    setCart({}); setTable(""); setNotes(""); setResult(null);
    setEditingId(null); setShowSummary(false); clientToken.current = "";
  };

  const submit = async () => {
    if (!table.trim()) { toast.error("Indica il numero del tavolo"); return; }
    if (items.length === 0) { toast.error("Aggiungi almeno un prodotto"); return; }
    if (sending) return;
    setSending(true);
    const payloadItems = items.map((i) => ({
      dish_id: i.id, name: i.name, quantity: i.quantity,
      notes: i.notes || "", course: i.course || 1,
    }));
    try {
      let data;
      if (editingId) {
        ({ data } = await api.put(`/preorder/${editingId}`, { items: payloadItems, notes }));
        toast.success("Modifica inviata al BAR.");
        setResult(data.preorder);
      } else {
        if (!clientToken.current) clientToken.current = uuid();
        ({ data } = await api.post("/preorder", {
          table: table.trim(), items: payloadItems, notes, client_token: clientToken.current,
        }));
        toast.success("Precomanda inviata al BAR.");
        setResult(data.preorder);
      }
      setShowSummary(false);
      setCart({}); setEditingId(null); clientToken.current = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const reprint = async (oid) => {
    try { await api.post(`/preorder/${oid}/reprint`); toast.success("Ristampa inviata al BAR."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (oid) => {
    if (!window.confirm("Annullare questa precomanda? Verrà stampato un avviso di annullamento al BAR.")) return;
    try { await api.post(`/preorder/${oid}/cancel`); toast.success("Annullamento inviato al BAR."); resetAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editFromResult = (pre) => {
    const c = {};
    (pre.items || []).forEach((i) => {
      const id = i.dish_id || i.name;
      c[id] = { id, name: i.name, price: i.price || 0, quantity: i.quantity, notes: i.notes || "", course: i.course || 1 };
    });
    setCart(c); setNotes(pre.notes || ""); setTable(pre.table || "");
    setEditingId(pre.order_id); setResult(null); setShowSummary(false);
    toast.info("Modifica la precomanda e invia di nuovo.");
  };

  // ---------------------------------------------------------------- SUCCESS
  if (result) {
    return (
      <div style={{ background: BG, fontFamily: "'Outfit', sans-serif" }} className="min-h-screen px-5 py-8" data-testid="preorder-success">
        <div className="max-w-md mx-auto">
          <div className="flex flex-col items-center text-center gap-3">
            <CheckCircle2 size={56} style={{ color: GOLD }} />
            <h1 className="text-2xl font-bold" style={{ color: DARK }}>Precomanda inviata al BAR</h1>
            <p className="text-sm text-black/60">Precomanda <b>#{result.number}</b> · Tavolo <b>{result.table}</b></p>
          </div>
          <div className="mt-6 bg-white rounded-2xl border border-black/[0.07] p-4 shadow-sm">
            {(result.items || []).map((i, idx) => (
              <div key={idx} className="flex justify-between py-1.5 text-[15px]" style={{ color: DARK }}>
                <span><b>{i.quantity}×</b> {i.name}{i.course === 2 ? " · dopo sbarra" : ""}</span>
              </div>
            ))}
            {result.notes ? <p className="mt-2 text-sm text-black/60">Note: {result.notes}</p> : null}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button data-testid="preorder-reprint-btn" onClick={() => reprint(result.order_id)} variant="outline" className="rounded-full h-12">
              <Printer size={18} className="mr-2" /> Ristampa
            </Button>
            <Button data-testid="preorder-edit-btn" onClick={() => editFromResult(result)} variant="outline" className="rounded-full h-12">
              <Pencil size={18} className="mr-2" /> Modifica
            </Button>
            <Button data-testid="preorder-cancel-btn" onClick={() => cancel(result.order_id)} variant="outline" className="rounded-full h-12 col-span-1 text-red-600 border-red-200">
              <Trash2 size={18} className="mr-2" /> Annulla
            </Button>
            <Button data-testid="preorder-new-btn" onClick={resetAll} className="rounded-full h-12" style={{ background: GOLD, color: DARK }}>
              <Plus size={18} className="mr-2" /> Nuova
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- MAIN
  return (
    <div style={{ background: BG, fontFamily: "'Outfit', sans-serif" }} className="min-h-screen pb-28" data-testid="waiter-preorder-page">
      <header className="sticky top-0 z-20 px-5 py-3 flex items-center justify-between" style={{ background: DARK }}>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Dogana 2.0</p>
          <h1 className="text-white text-lg font-semibold leading-tight">
            {editingId ? "Modifica precomanda" : "Precomanda Cameriere"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white/70 text-xs">{user?.name || user?.email}</span>
          <button data-testid="waiter-logout-btn" onClick={logout} className="text-white/70 p-2"><LogOut size={18} /></button>
        </div>
      </header>

      <div className="max-w-md mx-auto px-5 pt-5 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>Tavolo</label>
          <Input data-testid="preorder-table-input" value={table} onChange={(e) => setTable(e.target.value)}
            inputMode="numeric" placeholder="Es. 12"
            className="mt-1 h-14 text-2xl font-bold bg-white rounded-xl border-black/10" />
        </div>

        {menu.map((cat) => (
          <div key={cat.id}>
            <h2 className="text-base font-semibold mb-2" style={{ color: DARK }}>{cat.name}</h2>
            <div className="space-y-2">
              {cat.dishes.map((d) => {
                const line = cart[d.id];
                const q = line?.quantity || 0;
                return (
                  <div key={d.id} className="bg-white rounded-2xl border border-black/[0.07] p-3 shadow-sm" data-testid={`preorder-dish-${d.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium truncate" style={{ color: DARK }}>{d.name}</p>
                        <p className="text-sm text-black/50">{eur(d.price)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {q > 0 && (
                          <button data-testid={`preorder-minus-${d.id}`} onClick={() => bump(d, -1)}
                            className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center"><Minus size={16} /></button>
                        )}
                        {q > 0 && <span className="w-6 text-center font-bold text-lg" style={{ color: DARK }}>{q}</span>}
                        <button data-testid={`preorder-plus-${d.id}`} onClick={() => bump(d, 1)}
                          className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: GOLD, color: DARK }}><Plus size={16} /></button>
                      </div>
                    </div>
                    {q > 0 && (
                      <div className="mt-3 space-y-2">
                        <div className="flex gap-2">
                          {[1, 2].map((cv) => (
                            <button key={cv} data-testid={`preorder-course-${d.id}-${cv}`}
                              onClick={() => setField(d.id, "course", cv)}
                              className="flex-1 h-9 rounded-full text-xs font-semibold border"
                              style={(line?.course || 1) === cv
                                ? { background: GOLD, color: DARK, borderColor: GOLD }
                                : { background: "#fff", color: "#555", borderColor: "rgba(0,0,0,0.12)" }}>
                              {cv === 1 ? "1ª uscita" : "Dopo sbarra"}
                            </button>
                          ))}
                        </div>
                        <Input data-testid={`preorder-note-${d.id}`} value={line?.notes || ""}
                          onChange={(e) => setField(d.id, "notes", e.target.value)}
                          placeholder="Nota (es. senza cipolla)"
                          className="h-10 bg-[#faf8f3] rounded-lg border-black/10 text-sm" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* sticky bottom bar */}
      {count > 0 && !showSummary && (
        <div className="fixed bottom-0 inset-x-0 z-20 p-4" style={{ background: "linear-gradient(to top, rgba(246,243,236,1), rgba(246,243,236,0))" }}>
          <div className="max-w-md mx-auto">
            <Button data-testid="preorder-review-btn" onClick={() => setShowSummary(true)}
              className="w-full h-14 rounded-full text-base font-semibold shadow-lg" style={{ background: GOLD, color: DARK }}>
              Rivedi e invia · {count} {count === 1 ? "articolo" : "articoli"}
            </Button>
          </div>
        </div>
      )}

      {/* summary sheet */}
      {showSummary && (
        <div className="fixed inset-0 z-30 flex flex-col" style={{ background: BG }} data-testid="preorder-summary">
          <header className="px-5 py-3 flex items-center gap-3" style={{ background: DARK }}>
            <button onClick={() => setShowSummary(false)} className="text-white"><ChevronLeft size={22} /></button>
            <h2 className="text-white text-lg font-semibold">Riepilogo · Tavolo {table || "—"}</h2>
          </header>
          <div className="flex-1 overflow-auto px-5 py-5 max-w-md mx-auto w-full space-y-3">
            {[1, 2].map((cv) => {
              const list = items.filter((i) => (i.course || 1) === cv);
              if (list.length === 0) return null;
              return (
                <div key={cv}>
                  <p className="text-[11px] uppercase tracking-[0.2em] font-semibold mb-1" style={{ color: GOLD }}>
                    {cv === 1 ? "Prima uscita" : "Dopo sbarra"}
                  </p>
                  <div className="bg-white rounded-2xl border border-black/[0.07] p-3 shadow-sm">
                    {list.map((i) => (
                      <div key={i.id} className="py-1.5" style={{ color: DARK }}>
                        <div className="flex justify-between"><span><b>{i.quantity}×</b> {i.name}</span></div>
                        {i.notes ? <p className="text-xs text-black/55">— {i.notes}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>Note generali</label>
              <Textarea data-testid="preorder-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Note per l'operatore BAR" className="mt-1 bg-white rounded-xl border-black/10" />
            </div>
          </div>
          <div className="p-4 max-w-md mx-auto w-full">
            <Button data-testid="preorder-send-btn" onClick={submit} disabled={sending}
              className="w-full h-14 rounded-full text-base font-semibold shadow-lg" style={{ background: GOLD, color: DARK }}>
              {sending ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Send size={18} className="mr-2" />}
              {editingId ? "Invia modifica" : "INVIA PRECOMANDA"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
